import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { conversationLabel } from '../pages/chat/chatState';
import { newestLoopsFirst } from '../pages/loops/loopListOrder';
import { scheduleLabel } from '../pages/loops/loopSchedule';
import { assistantChatService } from '../services/assistantChatService';
import type { AssistantConversation } from '../services/assistantChatService';
import { loopService } from '../services/loopService';
import type { Loop } from '../services/loopService';
import { formatSupportTimeAgo } from '../utils/supportTime';

import './LandingRecentLists.css';

const MAX_ITEMS = 6;

interface RecentItem {
  id: string;
  to: string;
  icon: string;
  title: string;
  meta: string;
  live?: boolean;
}

type Loaded = { status: 'loading' } | { status: 'failed' } | { status: 'ready'; items: RecentItem[] };

const useRecentItems = (load: () => Promise<RecentItem[]>): Loaded => {
  const [state, setState] = useState<Loaded>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    load().then(
      (items) => { if (!cancelled) setState({ status: 'ready', items: items.slice(0, MAX_ITEMS) }); },
      () => { if (!cancelled) setState({ status: 'failed' }); },
    );

    return () => { cancelled = true; };
  }, [load]);

  return state;
};

const RecentList: React.FC<{
  heading: string;
  allTo: string;
  allLabel: string;
  empty: string;
  failed: string;
  state: Loaded;
}> = ({ heading, allTo, allLabel, empty, failed, state }) => (
  <div className="project-list landing-recent">
    <div className="project-list__heading-row">
      <h2 className="project-list__heading">{heading}</h2>
      <Link className="project-list__public-link" to={allTo}>{allLabel}</Link>
    </div>
    {state.status === 'loading' && (
      <div className="landing-recent__grid" aria-busy="true" aria-label={`Loading ${heading.toLowerCase()}`}>
        {[0, 1, 2].map((key) => <span key={key} className="landing-recent__placeholder" />)}
      </div>
    )}
    {state.status === 'failed' && <p className="landing-recent__empty">{failed}</p>}
    {state.status === 'ready' && state.items.length === 0 && <p className="landing-recent__empty">{empty}</p>}
    {state.status === 'ready' && state.items.length > 0 && (
      <ul className="landing-recent__grid">
        {state.items.map((item) => (
          <li key={item.id}>
            <Link className="landing-recent__item" to={item.to} title={item.title}>
              <span className="landing-recent__icon"><i className={`fa-solid ${item.icon}`} aria-hidden="true" /></span>
              <span className="landing-recent__text">
                <span className="landing-recent__title">{item.title}</span>
                <span className={`landing-recent__meta${item.live ? ' landing-recent__meta--live' : ''}`}>{item.meta}</span>
              </span>
              <i className="fa-solid fa-arrow-right landing-recent__arrow" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const loopItem = (loop: Loop): RecentItem => {
  const running = loop.active_run_status === 'running' || loop.active_run_status === 'queued';
  const waiting = loop.active_run_status === 'waiting_for_input';
  const meta = running ? 'Running now' : waiting ? 'Waiting for your answer'
    : loop.status === 'paused' ? 'Paused' : scheduleLabel(loop);

  return {
    id: loop.id,
    to: `/loops/${loop.id}`,
    icon: loop.schedule_kind === 'recurring' ? 'fa-repeat' : 'fa-bolt',
    title: loop.title,
    meta,
    live: running || waiting,
  };
};

const loadLoops = async (): Promise<RecentItem[]> => (
  newestLoopsFirst((await loopService.list()).filter((loop) => loop.status !== 'archived')).map(loopItem)
);

const lastActivity = (conversation: AssistantConversation): number => (
  Date.parse(conversation.last_message_at || conversation.updated_at) || 0
);

const loadChats = async (): Promise<RecentItem[]> => (
  [...await assistantChatService.listConversations()]
    .sort((left, right) => lastActivity(right) - lastActivity(left))
    .map((conversation) => ({
      id: conversation.id,
      to: `/chat/${conversation.id}`,
      icon: 'fa-comment',
      title: conversationLabel(conversation),
      meta: `${formatSupportTimeAgo(conversation.last_message_at || conversation.updated_at)} · ${conversation.message_count} ${
        conversation.message_count === 1 ? 'message' : 'messages'}`,
    }))
);

/** Loop mode on the landing page: the signed-in user's recent loops instead of projects. */
export const LandingLoopsList: React.FC = () => (
  <RecentList heading="My Loops" allTo="/loops" allLabel="All loops →" empty="Your loops will appear here."
    failed="Your loops could not be loaded." state={useRecentItems(loadLoops)} />
);

/** Chat mode on the landing page: the signed-in user's recent chats instead of projects. */
export const LandingChatsList: React.FC = () => (
  <RecentList heading="My Chats" allTo="/chat" allLabel="All chats →" empty="Your chats will appear here."
    failed="Your chats could not be loaded." state={useRecentItems(loadChats)} />
);
